import {
    ChangeDetectionStrategy,
    Component,
    Input,
    OnChanges,
    SimpleChanges,
    inject
} from "@angular/core";
import { ButtonComponent } from "@components/button/button.component";
import { CheckboxComponent } from "@components/checkbox/checkbox.component";
import { DateDrillDownComponent } from "@components/date-drill-down/date-drill-down.component";
import { FooterComponent } from "@components/footer/footer.component";
import { HotDocComponent } from "@components/hot-doc/hot-doc.component";
import { NgbTooltip } from "@ng-bootstrap/ng-bootstrap";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { BusyService } from "~app/common/components/busy/busy.service";
import { IDateDrillDownSelectedItem } from "~app/common/components/date-drill-down/components/date-drill-down-selector/models/dateDrillDownSelectedItem";
import { Cellstyle, NumericCellChangeArg } from "~app/common/components/hot-doc/hotdoc/cell";
import { HotDoc, IHotColumnDefinition, IHotDocSettings } from "~app/common/components/hot-doc/hotdoc/doc";
import { Expander } from "~app/common/components/hot-doc/hotdoc/expander";
import { HotRow } from "~app/common/components/hot-doc/hotdoc/row";
import { ToastService } from "~app/common/components/toast/toast.service";
import { AggregatPeriods, IAggregatPeriod } from "~app/common/models/aggregatperiod";
import { ForsorjningskallaAggregat } from "~app/common/models/forsorjningskalla";
import { CommandService } from "~app/common/services/cqs/command.service";
import { FordelaService } from "~app/common/services/fordela.service";
import { SetKundorderradLeveransradKvantitetCommand } from "~app/features/justera-leveransplan/common/commands/SetKundorderradLeveransradKvantitetCommand";
import { SetLeveransradKvantitetCommand } from "~app/features/justera-leveransplan/common/commands/SetLeveransradKvantitetCommand";
import { SetForsorjningsradPlanCommandFactory } from "~app/features/justera-leveransplan/common/commands/setForsorjningsradPlanCommands";
import { OverenskommentKvantitetCommandFactory } from "~app/features/justera-leveransplan/common/commands/setOverenskommenKvantitetCommands";
import { KundorderAggregat, KundorderradAggregat } from "~app/features/justera-leveransplan/common/models/jlp-kundorder.model";
import { IJlpResultSettings } from "~app/features/justera-leveransplan/common/models/jlp-model";
import { ForsorjningskallaCell } from "~app/features/justera-leveransplan/common/services/jlp-kundorder.hot-cells";
import { JlpPeriodService } from "~app/features/justera-leveransplan/common/services/jlp-period.service";
import { KundorderradCellComponent } from "~app/features/justera-leveransplan/common/services/kundorderrad-cell/kundorderrad-cell.component";
import { HotkeyDirective } from "../../../../../common/directives/hotkey/hotkey.directive";
import { GridPeriod, IOrderKundorderSearchResponse } from "../../../common/models/justeraLeveransplanModel";

/* eslint-disable no-bitwise */
@Component({
    selector: "rask-jlp-kundorder-result",
    templateUrl: "./jlp-kundorder-result.component.html",
    styleUrls: ["./jlp-kundorder-result.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        DateDrillDownComponent,
        CheckboxComponent,
        HotDocComponent,
        FooterComponent,
        ButtonComponent,
        HotkeyDirective,
        NgbTooltip,
        TranslatePipe
    ]
})
export class JlpKundorderResultComponent implements OnChanges {
    @Input() canEdit: boolean;
    currentPeriod: IAggregatPeriod = null;
    @Input() data: IOrderKundorderSearchResponse;
    hotDoc: HotDoc;
    @Input() period: GridPeriod;
    @Input() settings: IJlpResultSettings;
    showAvtaladVolym = false;
    showTakt = true;

    private readonly $translate = inject(TranslateService);
    private readonly busyService = inject(BusyService);
    private readonly commandService = inject(CommandService);
    private readonly fordelaKvantitetService = inject(FordelaService);
    private readonly jlpPeriodService = inject(JlpPeriodService);
    private readonly toastService = inject(ToastService);

    private aggregatePeriods: IAggregatPeriod[];
    private model: KundorderAggregat;

    constructor() {
        const commandService = this.commandService;

        this.commandService = commandService.createInstance();
    }

    canSave(): boolean {
        return this.commandService.isDirty();
    }

    dagarKvarInnevarandeManad(settings: IJlpResultSettings): number {
        return settings.totaltAntalArbetsdagarInnevarandeManad - Math.round(settings.passeradeAntalArbetsdagarInnevarandeManad);
    }

    initHotDoc = () => {
        this.prepareHotDoc(this.data);
        this.hotDoc = this.buildHotDoc();
    };

    isDirty(): boolean {
        return this.commandService.isDirty();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.settings !== undefined) {
            this.settings = changes.settings.currentValue;
        }

        this.initHotDoc();
    }

    onDateSelected(item: IDateDrillDownSelectedItem) {
        this.period = this.jlpPeriodService.onDateSelected(item);
        this.prepareHotDoc(this.data);
        this.hotDoc.rebind();
    }

    save(): void {
        this.busyService
            .addWork(this.commandService.pushCommandQueueTransaction())
            .subscribe(() => this.toastService.add("Ändringarna sparades"));
    }

    toggleAvtaladVolym() {
        this.hotDoc.rebind();
    }

    toggleTakt() {
        this.hotDoc.bind();
    }

    private readonly buildColumnDefinitions = (periods: IAggregatPeriod[]): IHotColumnDefinition[] => {
        const columns: IHotColumnDefinition[] = [];

        columns.push({
            header: "",
            width: 220,
            readOnly: true,
            cellName: "rowHeader1"
        });

        columns.push({
            header: "",
            width: 100,
            readOnly: true,
            cellName: "rowHeader2"
        });

        if (this.showTakt) {
            columns.push({
                header: "Takt",
                width: 60,
                readOnly: true,
                cellName: "takt",
                type: "numeric"
            });
        }

        const periodCols = periods.map(period => ({
            header: period.name,
            width: 95,
            cellName: period.key,
            type: "numeric"
        }));
        columns.push(...periodCols);

        columns.push({
            header: this.$translate.instant("KUNDORDER_LEVRADER_HEADER_SUMMA"),
            cellName: "summa",
            type: "numeric",
            width: 95
        });
        columns.push({
            header: this.$translate.instant("KUNDORDER_LEVRADER_HEADER_TRANSPORTERAT_LEVERANSRAD"),
            cellName: "transporteratleveransrad",
            type: "numeric",
            width: 140
        });
        columns.push({
            header: this.$translate.instant("KUNDORDER_LEVRADER_HEADER_OVERENSKOMMET"),
            cellName: "overenskommet",
            type: "numeric",
            width: 130
        });
        columns.push({
            header: this.$translate.instant("KUNDORDER_LEVRADER_HEADER_AVVIKELSE"),
            cellName: "avvikelse",
            type: "numeric",
            width: 95
        });

        return columns;
    };

    private readonly buildForsorjningsradAvtaladrad = (
        row: HotRow,
        model: ForsorjningskallaAggregat,
        kundorderrad: KundorderradAggregat,
        periods: IAggregatPeriod[]): void => {
        row.addEmpty("expanders", Cellstyle.Readonly | Cellstyle.NoRightBorder | Cellstyle.NoBottomBorder | Cellstyle.White);
        row.add("rowHeader1", ForsorjningskallaCell.create(model, { rowSpan: 3 }));
        row.addText("rowHeader2", "Avtalad", Cellstyle.Readonly | Cellstyle.NoBottomBorder | Cellstyle.White);

        let totaltAvtalatForPeriod = 0;
        periods.forEach(period => {
            const total = model.sumAvtalat(period);
            totaltAvtalatForPeriod += total;
            row.addNumeric(period.key, total, Cellstyle.Readonly);
        });

        row.addNumeric("summa", totaltAvtalatForPeriod, Cellstyle.Readonly | Cellstyle.Bold);
        row.addText("transporteratleveransrad", "", Cellstyle.Readonly);
        row.addText("overenskommet", "", Cellstyle.Readonly);
        row.addText("avvikelse", "", Cellstyle.Readonly);
    };

    private readonly onUpdateForsorjningsradPlan = (
        model: ForsorjningskallaAggregat,
        kundorderrad: KundorderradAggregat,
        period: IAggregatPeriod,
        change: NumericCellChangeArg) => {
        const periodStart = period.getStartOrLater(kundorderrad.periodFrom);
        const periodEnd = period.getEndOrEarlier(kundorderrad.periodTom);

        this.fordelaKvantitetService.getFordeladKvantitet(change.newValue, periodStart, periodEnd)
            .subscribe(result => {
                model.updateForsorjningsradPlan(result);

                const command = SetForsorjningsradPlanCommandFactory.createSetForsorjningsradPlanCommand(model, kundorderrad.kundorderradId, periodStart, periodEnd, result);
                this.commandService.addTransactionCommand(command);

                change.complete();
            });

    };

    private readonly onUpdateOverenskommenKvantitet = (model: ForsorjningskallaAggregat, kundorderrad: KundorderradAggregat, change: NumericCellChangeArg) => {
        model.overenskommet = change.newValue;

        const command = OverenskommentKvantitetCommandFactory.createSetOverenskommentKvantitetCommand(model, kundorderrad.kundorderradId, change.newValue);
        this.commandService.addTransactionCommand(command);

        change.complete();
    };

    private readonly buildForsorjningsradPlanrad = (row: HotRow, model: ForsorjningskallaAggregat, kundorderrad: KundorderradAggregat, periods: IAggregatPeriod[]): void => {
        row.addEmpty("expanders", Cellstyle.Readonly | Cellstyle.NoRightBorder | Cellstyle.NoBottomBorder | Cellstyle.White);
        if (!this.showAvtaladVolym) {
            row.add("rowHeader1", ForsorjningskallaCell.create(model, { rowSpan: 2 }));
        }
        row.addText("rowHeader2", this.$translate.instant("KUNDORDER_LEVRADER_HEADER_PLAN"), Cellstyle.Readonly | Cellstyle.NoBottomBorder | Cellstyle.White);

        const percentSettings = { noZero: true };
        row.addPercent("takt",
            model.calculateTakt(this.currentPeriod,
                this.settings.passeradeAntalArbetsdagarInnevarandeManad,
                this.settings.totaltAntalArbetsdagarInnevarandeManad),
            Cellstyle.Readonly,
            percentSettings);

        periods.forEach(period => {
            const total = model.sumPlan(period);
            const isActive = period.isWithin(kundorderrad.periodFrom, kundorderrad.periodTom);
            const style = isActive ? 0 : Cellstyle.Readonly;
            row.addNumeric(period.key, total, style, change => this.onUpdateForsorjningsradPlan(model, kundorderrad, period, change));
        });

        row.addText("summa", "", Cellstyle.Readonly);
        row.addNumeric("transporteratleveransrad", model.sumTransporteratPlanerat(periods), Cellstyle.Readonly);
        row.addNumeric("overenskommet", model.overenskommet, null, change => this.onUpdateOverenskommenKvantitet(model, kundorderrad, change));
        row.addNumeric("avvikelse", model.avvikelse(periods), Cellstyle.Readonly);
    };

    private readonly buildForsorjningsradTransporteratrad = (row: HotRow, model: ForsorjningskallaAggregat, periods: IAggregatPeriod[]): void => {
        row.addEmpty("expanders", Cellstyle.Readonly | Cellstyle.NoRightBorder | Cellstyle.White);
        row.addText("rowHeader2", this.$translate.instant("KUNDORDER_LEVRADER_HEADER_TRANSPORTERAT"), Cellstyle.Readonly | Cellstyle.White);

        let grandtotal = 0;
        periods.forEach(period => {
            const total = model.sumTransporterat(period);
            grandtotal += total;
            row.addNumeric(period.key, total, Cellstyle.Readonly);
        });

        row.addNumeric("summa", grandtotal, Cellstyle.Readonly | Cellstyle.Bold);
        row.addText("transporteratleveransrad", "", Cellstyle.Readonly);
        row.addText("overenskommet", "", Cellstyle.Readonly);
        row.addText("avvikelse", "", Cellstyle.Readonly);
    };

    private readonly buildKundorderradAvtaladrad = (row: HotRow, expander: Expander, model: KundorderradAggregat, periods: IAggregatPeriod[]): void => {
        row.addExpander(expander, Cellstyle.Readonly | Cellstyle.NoRightBorder | Cellstyle.AlignLeft, { rowSpan: 4 });
        row.addAngularComponent("rowHeader1", KundorderradCellComponent, (instance) => {
            instance.applyData(model);
        }, Cellstyle.Readonly | Cellstyle.NoRightBorder, { rowSpan: 4 });
        row.addText("rowHeader2", "Avtalad", Cellstyle.Readonly | Cellstyle.NoBottomBorder);

        row.addEmpty("takt", Cellstyle.Readonly); // Behövs för rad-style

        let totaltAvtaladAllaPerioder = 0;
        periods.forEach(period => {
            const avtaladForPeriod = model.sumAvtalat(period);
            totaltAvtaladAllaPerioder += avtaladForPeriod;

            row.addNumeric(period.key, avtaladForPeriod, Cellstyle.Readonly);
        });

        row.addNumeric("summa", totaltAvtaladAllaPerioder, Cellstyle.Readonly | Cellstyle.Bold);
        row.addEmpty("transporteratleveransrad", Cellstyle.Readonly); // Behövs för rad-style
        row.addEmpty("overenskommet", Cellstyle.Readonly); // Behövs för rad-style
        row.addEmpty("avvikelse", Cellstyle.Readonly); // Behövs för rad-style

    };

    private readonly buildKundorderradBalansrad = (row: HotRow, model: KundorderradAggregat, periods: IAggregatPeriod[]): void => {
        row.addEmpty("expanders", Cellstyle.Readonly | Cellstyle.NoRightBorder);
        row.addEmpty("rowHeader1", Cellstyle.Readonly | Cellstyle.NoBottomBorder);
        row.addText("rowHeader2", this.$translate.instant("KUNDORDER_LEVRADER_HEADER_BALANS"), Cellstyle.Readonly);

        let grandtotal = 0;
        periods.forEach(period => {
            const balans = model.sumBalans(period);
            grandtotal += balans;

            const style = Cellstyle.Readonly | (period.isCurrentlyActive ? Cellstyle.ActivePeriod : 0);
            row.addNumeric(period.key, balans, style);
        });

        row.addNumeric("summa", grandtotal, Cellstyle.Readonly | Cellstyle.Bold);
        row.addText("transporteratleveransrad", "", Cellstyle.Readonly);
        row.addText("overenskommet", null, Cellstyle.Readonly);
        row.addText("avvikelse", "", Cellstyle.Readonly);
    };

    private readonly onUpdateKundorderradKvantitet = (change: NumericCellChangeArg, model: KundorderradAggregat) => {
        model.overenskommet = change.newValue;

        this.commandService.addTransactionCommand(new SetKundorderradLeveransradKvantitetCommand(c => {
            c.kundorderradId = model.kundorderradId;
            c.overenskommenKvantitetLeveransrad = change.newValue;
        }));

        change.complete();
    };

    private readonly onUpdateLeveransradKvantitet = (change: NumericCellChangeArg, model: KundorderradAggregat, period: IAggregatPeriod) => {
        const periodStart = period.getStartOrLater(model.periodFrom);
        const periodEnd = period.getEndOrEarlier(model.periodTom);

        this.fordelaKvantitetService.getFordeladKvantitet(change.newValue, periodStart, periodEnd)
            .subscribe(result => {
                model.updateLeveransradKvantitet(result);

                this.commandService.addTransactionCommand(new SetLeveransradKvantitetCommand(c => {
                    c.kundorderradId = model.kundorderradId;
                    c.periodStart = periodStart;
                    c.periodEnd = periodEnd;
                    c.fordeladKvantitet = result;
                }));

                change.complete();
            });
    };

    private readonly buildKundorderradLeveransrad = (row: HotRow, expander: Expander, model: KundorderradAggregat, periods: IAggregatPeriod[]): void => {
        if (this.showAvtaladVolym) {
            row.addEmpty("settings", Cellstyle.Readonly); // Behövs för rad-style
            row.addEmpty("rowHeader1", Cellstyle.Readonly); // Behövs för rad-style
        } else {
            row.addExpander(expander, Cellstyle.Readonly | Cellstyle.NoRightBorder | Cellstyle.AlignLeft, { rowSpan: 3 });
            row.addAngularComponent("rowHeader1", KundorderradCellComponent, (instance) => {
                instance.applyData(model);
            }, Cellstyle.Readonly | Cellstyle.NoRightBorder, { rowSpan: 3 });
        }
        row.addText("rowHeader2", this.$translate.instant("KUNDORDER_LEVRADER_HEADER_LEVERANSRAD"), Cellstyle.Readonly | Cellstyle.NoBottomBorder);

        const percentSettings = { noZero: true };
        row.addPercent("takt",
            model.calculateTaktLeverans(this.currentPeriod,
                this.settings.passeradeAntalArbetsdagarInnevarandeManad,
                this.settings.totaltAntalArbetsdagarInnevarandeManad),
            Cellstyle.Readonly,
            percentSettings);

        let grandtotal = 0;
        periods.forEach(period => {
            const sumLeveransrad = model.sumLeveransrad(period);
            grandtotal += sumLeveransrad;

            const isActive = period.isWithin(model.periodFrom, model.periodTom);
            const style = isActive ? Cellstyle.Default : Cellstyle.Readonly;
            row.addNumeric(period.key, sumLeveransrad, style, arg => this.onUpdateLeveransradKvantitet(arg, model, period));
        });

        row.addNumeric("summa", grandtotal, Cellstyle.Readonly | Cellstyle.Bold);
        row.addNumeric("transporteratleveransrad", model.sumTransporteratLeveransrad(periods), Cellstyle.Readonly);
        row.addNumeric("overenskommet", model.overenskommet, Cellstyle.Default, change => this.onUpdateKundorderradKvantitet(change, model));
        row.addNumeric("avvikelse", model.avvikelse(periods), Cellstyle.Readonly);
    };

    private readonly buildKundorderradTransporteratrad = (row: HotRow, model: KundorderradAggregat, periods: IAggregatPeriod[]): void => {
        row.addEmpty("expanders", Cellstyle.Readonly | Cellstyle.NoRightBorder);
        row.addText("rowHeader2", this.$translate.instant("KUNDORDER_LEVRADER_HEADER_TRANSPORTERAT"), Cellstyle.Readonly | Cellstyle.NoBottomBorder);

        let grandtotal = 0;
        periods.forEach(period => {
            const transporterat = model.sumTransporterat(period);
            grandtotal += transporterat;

            // Jämför mot kundorderradens aggregat för aktuella försörjningskällor
            const aktuellaForsorjningskallor = model.forsorjningskallor.map(kalla => kalla.id);
            const transporteratForsorjningskallor = model.sumTransporteratForsorjningskallor(period, aktuellaForsorjningskallor);

            const flash = transporteratForsorjningskallor !== transporterat ? Cellstyle.Flash : Cellstyle.Default;
            row.addNumeric(period.key, transporterat, Cellstyle.Readonly | flash);
        });

        row.addNumeric("summa", grandtotal, Cellstyle.Readonly | Cellstyle.Bold);
    };

    private readonly buildSummaAvtalad = (row: HotRow, model: KundorderAggregat, periods: IAggregatPeriod[]) => {
        row.addEmpty("expanders", Cellstyle.Readonly | Cellstyle.NoRightBorder | Cellstyle.NoBottomBorder);
        row.addText("rowHeader1", "SUMMA", Cellstyle.Readonly | Cellstyle.Bold | Cellstyle.NoRightBorder | Cellstyle.NoBottomBorder | Cellstyle.Uppercase);
        row.addText("rowHeader2", "Avtalad", Cellstyle.Readonly | Cellstyle.Bold | Cellstyle.NoBottomBorder);
        row.addEmpty("takt", Cellstyle.Readonly);

        let totalAvtalad = 0;
        periods.forEach(period => {
            const avtalad = model.sumAvtalad(period);
            totalAvtalad += avtalad;
            row.addNumeric(period.key, avtalad, Cellstyle.Readonly | Cellstyle.Bold | Cellstyle.Uppercase);
        });
        row.addNumeric("summa", totalAvtalad, Cellstyle.Readonly | Cellstyle.Bold);
        row.addText("transporteratleveransrad", "", Cellstyle.Readonly | Cellstyle.Uppercase);
        row.addText("overenskommet", "", Cellstyle.Readonly | Cellstyle.Uppercase);
        row.addText("avvikelse", "", Cellstyle.Readonly | Cellstyle.Uppercase);
    };

    private readonly buildSummaBalans = (row: HotRow, model: KundorderAggregat, periods: IAggregatPeriod[]) => {
        row.addEmpty("expanders", Cellstyle.Readonly | Cellstyle.NoRightBorder | Cellstyle.NoBottomBorder);
        row.addEmpty("rowHeader1", Cellstyle.Readonly | Cellstyle.NoRightBorder | Cellstyle.NoBottomBorder);
        row.addText("rowHeader2", this.$translate.instant("KUNDORDER_LEVRADER_HEADER_SUMMA_BALANS"), Cellstyle.Readonly | Cellstyle.Bold | Cellstyle.NoBottomBorder);

        let grandtotal = 0;
        periods.forEach(period => {
            const balans = model.sumBalans(period);
            grandtotal += balans;

            const style = Cellstyle.Readonly | Cellstyle.Bold | (period.isCurrentlyActive ? Cellstyle.ActivePeriod : 0);
            row.addNumeric(period.key, balans, style);
        });
        row.addNumeric("summa", grandtotal, Cellstyle.Readonly | Cellstyle.Bold);
    };

    private readonly buildSummaLeverans = (row: HotRow, model: KundorderAggregat, periods: IAggregatPeriod[]) => {
        row.addEmpty("expanders", Cellstyle.Readonly | Cellstyle.NoRightBorder | Cellstyle.NoBottomBorder);
        if (this.showAvtaladVolym) {
            row.addEmpty("rowHeader1", Cellstyle.Readonly | Cellstyle.NoRightBorder | Cellstyle.NoBottomBorder);
            row.addText("rowHeader2", this.$translate.instant("KUNDORDER_LEVRADER_HEADER_SUMMA_LEVERANS"), Cellstyle.Readonly | Cellstyle.Bold | Cellstyle.NoBottomBorder);
        } else {
            row.addText("rowHeader1",
                this.$translate.instant("KUNDORDER_LEVRADER_HEADER_SUMMA"),
                Cellstyle.Readonly | Cellstyle.Bold | Cellstyle.NoRightBorder | Cellstyle.NoBottomBorder | Cellstyle.Uppercase);
            row.addText("rowHeader2", this.$translate.instant("KUNDORDER_LEVRADER_HEADER_SUMMA_LEVERANS"), Cellstyle.Readonly | Cellstyle.NoBottomBorder | Cellstyle.Bold);
        }

        const takt = model.calculateTaktLeverans(this.currentPeriod, this.settings.passeradeAntalArbetsdagarInnevarandeManad, this.settings.totaltAntalArbetsdagarInnevarandeManad);

        row.addPercent("takt", takt, Cellstyle.Readonly, { noZero: true });

        let grandtotal = 0;
        periods.forEach(period => {
            const sumLeveransrad = model.sumLeveransrad(period);
            grandtotal += sumLeveransrad;
            row.addNumeric(period.key, sumLeveransrad, Cellstyle.Readonly | Cellstyle.Bold);
        });

        row.addNumeric("summa", grandtotal, Cellstyle.Readonly | Cellstyle.Bold);
        row.addNumeric("transporteratleveransrad", model.sumTransporteratLeveransrad(periods), Cellstyle.Readonly);
        row.addNumeric("overenskommet", model.sumOverenskommet(), Cellstyle.Readonly);
        row.addText("avvikelse", "", Cellstyle.Readonly);
    };

    private readonly buildSummaPlan = (row: HotRow, model: KundorderAggregat, periods: IAggregatPeriod[]) => {
        row.addEmpty("expanders", Cellstyle.Readonly | Cellstyle.NoRightBorder | Cellstyle.NoBottomBorder);
        row.addEmpty("rowHeader1", Cellstyle.Readonly | Cellstyle.NoRightBorder | Cellstyle.NoBottomBorder);
        row.addText("rowHeader2", this.$translate.instant("KUNDORDER_LEVRADER_HEADER_SUMMA_PLAN"), Cellstyle.Readonly | Cellstyle.Bold | Cellstyle.NoBottomBorder);

        const takt = model.calculateTakt(this.currentPeriod, this.settings.passeradeAntalArbetsdagarInnevarandeManad, this.settings.totaltAntalArbetsdagarInnevarandeManad);

        row.addPercent("takt", takt, Cellstyle.Readonly, { noZero: true });

        let grandtotal = 0;
        periods.forEach(period => {
            const sumPeriod = model.sumPlan(period);
            grandtotal += sumPeriod;
            row.addNumeric(period.key, sumPeriod, Cellstyle.Readonly | Cellstyle.Bold);
        });

        row.addNumeric("summa", grandtotal, Cellstyle.Readonly | Cellstyle.Bold);
    };

    private readonly buildSummaTransporterat = (row: HotRow, model: KundorderAggregat, periods: IAggregatPeriod[]) => {
        row.addEmpty("expanders", Cellstyle.Readonly | Cellstyle.NoRightBorder);
        row.addEmpty("rowHeader1", Cellstyle.Readonly | Cellstyle.NoRightBorder);
        row.addText("rowHeader2", this.$translate.instant("KUNDORDER_LEVRADER_HEADER_SUMMA_TRANSPORTERAT"), Cellstyle.Readonly | Cellstyle.Bold);

        let grandtotal = 0;
        periods.forEach(period => {
            const sumPeriod = model.sumTransporterat(period);
            grandtotal += sumPeriod;
            row.addNumeric(period.key, sumPeriod, Cellstyle.Readonly | Cellstyle.Bold);
        });

        row.addNumeric("summa", grandtotal, Cellstyle.Readonly | Cellstyle.Bold);
    };

    private readonly buildHotDoc = (): HotDoc => {
        const hotDocSettings: IHotDocSettings = {
            raskSettings: {
                expander: {
                    col: 0,
                    name: "expanders"
                },
                calculateSum: true
            },
            fixedColumnsStart: 3
        };

        const colFactory = () => this.buildColumnDefinitions(this.aggregatePeriods);

        const rowFactory = (doc: HotDoc) => {
            const model = this.model;
            const periods = this.aggregatePeriods;

            model.kundorderrader.forEach(kundorderrad => {
                const expander = doc.createExpander(kundorderrad.kundorderradId);

                if (this.showAvtaladVolym) {
                    doc.createRow(row => this.buildKundorderradAvtaladrad(row, expander, kundorderrad, periods), Cellstyle.StrongTop);
                }
                doc.createRow(row => this.buildKundorderradLeveransrad(row, expander, kundorderrad, periods), (!this.showAvtaladVolym ? Cellstyle.StrongTop : Cellstyle.Default));
                doc.createRow(row => this.buildKundorderradTransporteratrad(row, kundorderrad, periods));
                doc.createRow(row => this.buildKundorderradBalansrad(row, kundorderrad, periods));

                if (expander.expanded) {
                    kundorderrad.forsorjningskallor.forEach(forsorjningskalla => {
                        if (this.showAvtaladVolym) {
                            // eslint-disable-next-line sonarjs/no-nested-functions
                            doc.createRow(row => this.buildForsorjningsradAvtaladrad(row, forsorjningskalla, kundorderrad, periods), Cellstyle.Default);
                        }
                        // eslint-disable-next-line sonarjs/no-nested-functions
                        doc.createRow(row => this.buildForsorjningsradPlanrad(row, forsorjningskalla, kundorderrad, periods), Cellstyle.Default);
                        // eslint-disable-next-line sonarjs/no-nested-functions
                        doc.createRow(row => this.buildForsorjningsradTransporteratrad(row, forsorjningskalla, periods), Cellstyle.Default);
                    });
                }
            });

            if (this.showAvtaladVolym) {
                doc.createRow(row => this.buildSummaAvtalad(row, model, periods), Cellstyle.StrongTop);
            }
            doc.createRow(row => this.buildSummaLeverans(row, model, periods), (!this.showAvtaladVolym ? Cellstyle.StrongTop : Cellstyle.Default));
            doc.createRow(row => this.buildSummaBalans(row, model, periods));
            doc.createRow(row => this.buildSummaPlan(row, model, periods));
            doc.createRow(row => this.buildSummaTransporterat(row, model, periods));
        };

        return new HotDoc(colFactory, rowFactory, () => hotDocSettings);
    };

    private prepareHotDoc(res: IOrderKundorderSearchResponse) {
        this.aggregatePeriods = AggregatPeriods.create("sv-SE", this.period);
        this.currentPeriod = this.aggregatePeriods.find(a => a.isCurrentlyActive) || null;
        this.model = new KundorderAggregat(res.kundorderrader);
    }
}
